import { Languages, Moon, Sun } from 'lucide-react'
import { useAppStore } from '@/app/providers/app-store'
import { cn } from '@/shared/lib/cn'

interface ToggleButtonProps {
  className?: string
  size?: 'sm' | 'md'
}

const sizeMap = {
  sm: 'h-9 w-9',
  md: 'h-10 w-10',
}

/**
 * Theme toggle (light ↔ dark). Honors §6.7.5 — instant transition, no
 * elaborate animation that would fight reduced-motion users.
 */
export function ThemeToggle({ className, size = 'md' }: ToggleButtonProps) {
  const theme = useAppStore((s) => s.theme)
  const toggle = useAppStore((s) => s.toggleTheme)

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
      className={cn(
        'relative inline-flex items-center justify-center rounded-full',
        'border border-border bg-card/60 backdrop-blur-md',
        'text-foreground/80 hover:text-foreground hover:border-primary/50',
        'transition-colors duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        sizeMap[size],
        className,
      )}
    >
      <Sun
        className={cn(
          'absolute h-4 w-4 transition-all duration-300',
          theme === 'dark'
            ? 'scale-0 rotate-90 opacity-0'
            : 'scale-100 rotate-0 opacity-100',
        )}
      />
      <Moon
        className={cn(
          'absolute h-4 w-4 transition-all duration-300',
          theme === 'dark'
            ? 'scale-100 rotate-0 opacity-100'
            : 'scale-0 -rotate-90 opacity-0',
        )}
      />
    </button>
  )
}

/**
 * Locale toggle (AR ↔ EN). Same visual language as the theme toggle.
 */
export function LocaleToggle({ className, size = 'md' }: ToggleButtonProps) {
  const locale = useAppStore((s) => s.locale)
  const toggle = useAppStore((s) => s.toggleLocale)

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={locale === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}
      className={cn(
        'relative inline-flex items-center justify-center gap-1.5 rounded-full',
        'border border-border bg-card/60 backdrop-blur-md px-3',
        'text-xs font-semibold uppercase tracking-wider',
        'text-foreground/80 hover:text-foreground hover:border-primary/50',
        'transition-colors duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        size === 'sm' ? 'h-9' : 'h-10',
        className,
      )}
    >
      <Languages className="h-3.5 w-3.5" aria-hidden />
      <span className="font-headline" style={{ letterSpacing: '0.06em' }}>
        {locale === 'ar' ? 'EN' : 'AR'}
      </span>
    </button>
  )
}
